import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { progressAPI } from '../services/api';
import LoadingScreen from './LoadingScreen';
import BenchmarkCard from './BenchmarkCard';
import styles from '../styles/ProgressDashboard.module.css';

// Lazy load heavy chart components (Nivo charts are ~100KB)
const MilestoneActivityGraph = lazy(() => import('./MilestoneActivityGraph'));
const JourneyInsightsCard = lazy(() => import('./JourneyInsightsCard'));
// Keep BehavioralTimeline for potential future use
// const BehavioralTimeline = lazy(() => import('./BehavioralTimeline'));

/**
 * Progress Dashboard Component
 * Displays comprehensive user progress including mission, stats, graphs, and insights
 *
 * Props:
 * - onBack: Callback to navigate back
 * - onNavigateToTasks: Callback to navigate to action plan view
 */
function ProgressDashboard({ onBack, onNavigateToTasks }) {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load progress stats
  const loadStats = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      const data = await progressAPI.getStats();
      setStats(data);
    } catch (err) {
      console.error('[ProgressDashboard] Error loading stats:', err);
      setError(err.message || 'Failed to load progress data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Loading state
  if (isLoading) {
    return <LoadingScreen text="Loading your progress..." variant="dots" showImage={true} />;
  }

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Unable to Load Progress</h2>
          <p>{error}</p>
          <div className={styles.buttonGroup}>
            <button onClick={loadStats} className={styles.retryButton}>
              Retry
            </button>
            <button onClick={onBack} className={styles.backButton}>
              Back to Chat
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    currentMission,
    recentWins,
    whatsNext,
    milestoneActivity
  } = stats || {};

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button onClick={onBack} className={styles.backButtonHeader} aria-label="Back">
          ← Back
        </button>
        <h1 className={styles.title}>Your Journey</h1>
      </div>

      {/* Scrollable content */}
      <div className={styles.content}>

        {/* Current Mission Hero */}
        {currentMission && (
          <div className={styles.heroSection}>
            <div className={styles.heroContent}>
              <p className={styles.heroLabel}>Current Goal</p>
              <h2 className={styles.heroTitle}>{currentMission.title}</h2>
              <div className={styles.progressBarContainer}>
                <div className={styles.progressBarBackground}>
                  <div
                    className={styles.progressBarFill}
                    style={{ width: `${currentMission.progress}%` }}
                  />
                </div>
                <p className={styles.progressText}>
                  {currentMission.completedTasks} of {currentMission.totalRequiredTasks} required tasks complete
                  <span className={styles.progressPercent}> ({currentMission.progress}%)</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Benchmarking / Community Comparison */}
        <BenchmarkCard onError={(err) => console.error('[ProgressDashboard] Benchmark error:', err)} />

        {/* Journey Insights - LLM-generated behavioral narrative */}
        <Suspense fallback={<div className={styles.chartLoading}>Loading insights...</div>}>
          <JourneyInsightsCard />
        </Suspense>

        {/* Milestone Activity Graph */}
        <Suspense fallback={<div className={styles.chartLoading}>Loading chart...</div>}>
          <MilestoneActivityGraph data={milestoneActivity || []} />
        </Suspense>

        {/* Behavioral Timeline - Kept for potential future use */}
        {/* <Suspense fallback={<div className={styles.chartLoading}>Loading timeline...</div>}>
          <BehavioralTimeline timeline={behavioralTimeline || []} />
        </Suspense> */}

        {/* Recent Wins */}
        {recentWins && recentWins.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Recent Wins</h3>
            <div className={styles.winsList}>
              {recentWins.map((win, index) => (
                <div key={index} className={styles.winItem}>
                  <div className={styles.winCheck}>✓</div>
                  <div className={styles.winContent}>
                    <p className={styles.winTask}>{win.task}</p>
                    {win.completed_at && (
                      <p className={styles.winDate}>
                        {new Date(win.completed_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What's Next */}
        {whatsNext && whatsNext.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>What's Next</h3>
            <div className={styles.nextList}>
              {whatsNext.map((task, index) => (
                <div key={index} className={styles.nextItem}>
                  <div className={styles.nextBullet}>•</div>
                  <div className={styles.nextContent}>
                    <p className={styles.nextTask}>{task.task}</p>
                    {task.category && (
                      <p className={styles.nextCategory}>{task.category}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={onNavigateToTasks} className={styles.viewAllButton}>
              View Full Action Plan →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

export default ProgressDashboard;
